import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { pollTask } from 'ember-lifeline';
import { t } from 'ember-intl';
import type Owner from '@ember/owner';
import type IntlService from 'ember-intl/services/intl';
import { s3PresignedUrlExpiresAt } from 'scaleflow/utils/s3-presigned-url-expired';
import Clock from 'scaleflow/components/tailwind-icons/clock';
import ExclamationCircle from 'scaleflow/components/tailwind-icons/exclamation-circle';

/** How often the countdown re-reads the clock. */
const TICK_MS = 15_000;
const MINUTE_MS = 60_000;

export interface ScansCredentialsValiditySignature {
  Element: HTMLSpanElement;
  Args: {
    Named: {
      /** A presigned S3 URL, or nothing when no link has been generated yet. */
      url?: string | null;
    };
  };
}

/**
 * Badge showing how much longer a presigned report link stays valid.
 *
 * Renders nothing at all when there is no link — the absence of a badge is the
 * signal that nothing has been generated yet.
 */
export default class ScansCredentialsValidity extends Component<ScansCredentialsValiditySignature> {
  @service declare intl: IntlService;

  @tracked private now = Date.now();

  private timer?: ReturnType<typeof setTimeout>;

  constructor(
    owner: Owner,
    args: ScansCredentialsValiditySignature['Args']['Named'],
  ) {
    super(owner, args);

    /**
     * `pollTask` rather than a bare interval because it stops recursing under
     * `Ember.testing`: the tick below is the only thing that writes tracked
     * state off a timer, and a tracked write with no runloop around it trips the
     * autorun assertion. In tests `next` merely queues the tick for
     * `pollTaskFor`, so it can never fire on its own.
     *
     * The re-arm is a native `setTimeout` and not lifeline's `runTask`: an Ember
     * timer is a pending timer, so `settled()` would block for a full `TICK_MS`
     * in every test that renders this component.
     */
    pollTask(this, (next: () => void) => {
      this.now = Date.now();
      this.timer = setTimeout(next, TICK_MS);
    });
  }

  willDestroy() {
    super.willDestroy();
    clearTimeout(this.timer);
  }

  get hasLink(): boolean {
    return Boolean(this.args.url);
  }

  private get expiresAt(): Date | null {
    return s3PresignedUrlExpiresAt(this.args.url);
  }

  /** Validity left in milliseconds; 0 for a link with no readable expiry. */
  private get remainingMs(): number {
    const expiresAt = this.expiresAt;

    if (!expiresAt) {
      return 0;
    }

    return Math.max(0, expiresAt.getTime() - this.now);
  }

  get isExpired(): boolean {
    return this.remainingMs <= 0;
  }

  /**
   * Hours once there is more than an hour left, minutes below that. Every
   * conversion floors: overstating how long a link lasts is the one error that
   * sends someone to a download that has already stopped working.
   */
  get validityLabel(): string {
    const minutes = Math.floor(this.remainingMs / MINUTE_MS);

    if (minutes > 60) {
      return this.intl.t('scans.processing.credentials-valid-hours', {
        count: Math.floor(minutes / 60),
      });
    }

    if (minutes < 1) {
      return this.intl.t('scans.processing.credentials-valid-soon');
    }

    return this.intl.t('scans.processing.credentials-valid-minutes', {
      count: minutes,
    });
  }

  <template>
    {{! `data-validity` rather than the app's usual `test-id`: strict-mode
    templates are type-checked and Glint only admits `data-*` for attributes
    outside the HTML spec. It matches the sibling `Scans::StatusDot`'s
    `data-tone` either way. }}
    {{#if this.hasLink}}
      <span
        class="px-2.5 py-0.5 rounded-full text-xs font-medium flex items-center gap-1
          {{if
            this.isExpired
            'bg-red-100 text-red-600'
            'bg-green-100 text-green-600'
          }}"
        data-validity={{if this.isExpired "expired" "valid"}}
        ...attributes
      >
        {{#if this.isExpired}}
          <ExclamationCircle class="w-3 h-3" />
          {{t "scans.processing.refresh-credentials"}}
        {{else}}
          <Clock class="w-3 h-3" />
          {{this.validityLabel}}
        {{/if}}
      </span>
    {{/if}}
  </template>
}
